import { PersonalAccessTokenSource } from "@/gql/schema";

const API_URL = new URL(import.meta.env.VITE_API_URL);

export type AuthOrganisation = {
  id: string;
  name: string;
  is_admin: boolean;
  profile_picture_url: string | null;
};

export type AuthUser = {
  id: string;
  name: string | null;
  email: string;
  pending_email: string | null;
  primary_phone: string | null;
  date_of_birth: string | null;
  has_password: boolean;
  is_admin: boolean;
  email_verified: boolean;
  profile_picture_url: string | null;
  notification_settings: {
    gets_email_notifications: boolean;
    gets_push_notifications: boolean;
  };
};

type AuthResponse = {
  token: string;
  user: AuthUser;
};

export type MeResponse = {
  user: AuthUser;
};

type OrganisationsResponse = {
  organisations: AuthOrganisation[];
};

async function parseErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const data = (await response.json()) as { message?: string };
    return data.message || fallback;
  } catch {
    return `${fallback} (HTTP ${response.status})`;
  }
}

export async function requestCode(email: string): Promise<void> {
  const response = await fetch(new URL(`/api/auth/request-code`, API_URL), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, "Failed to send code"));
  }
}

export async function verifyCode(
  email: string,
  code: string,
): Promise<AuthResponse> {
  return signIn({ email, code }, "Invalid or expired code");
}

/** Raised when a password sign in hits an account that never confirmed its email. */
export class RequiresVerificationError extends Error {}

/** One endpoint takes either proof - a password or an emailed code. */
async function signIn(
  credentials:
    | { email: string; password: string }
    | { email: string; code: string },
  fallbackMessage: string,
): Promise<AuthResponse> {
  const response = await fetch(new URL(`/api/auth/login`, API_URL), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      ...credentials,
      source: PersonalAccessTokenSource.Admin,
    }),
  });

  if (!response.ok) {
    const cloned = response.clone();
    const body = (await cloned.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;

    if (body.requires_verification) {
      throw new RequiresVerificationError(
        await parseErrorMessage(response, fallbackMessage),
      );
    }

    throw new Error(await parseErrorMessage(response, fallbackMessage));
  }

  return response.json() as Promise<AuthResponse>;
}

export async function loginWithPassword(
  email: string,
  password: string,
): Promise<AuthResponse> {
  return signIn({ email, password }, "Invalid email or password");
}

export type RegisterResponse = {
  message: string;
  email: string;
};

/**
 * Creates the account but does not sign you in - the emailed code does that,
 * so the first session doubles as proof the address is real.
 */
export async function register(
  email: string,
  name: string,
  password: string,
): Promise<RegisterResponse> {
  const response = await fetch(new URL(`/api/auth/register`, API_URL), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email,
      name,
      password,
      password_confirmation: password,
      source: PersonalAccessTokenSource.Admin,
    }),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, "Registration failed"));
  }

  return response.json() as Promise<RegisterResponse>;
}

export async function acceptInvitation(token: string): Promise<AuthResponse> {
  const response = await fetch(
    new URL(`/api/auth/accept-invitation`, API_URL),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ token }),
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Failed to accept invitation"),
    );
  }

  return response.json() as Promise<AuthResponse>;
}

export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(new URL(`/api/auth/forgot-password`, API_URL), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Failed to send reset code"),
    );
  }
}

export async function resetPassword(
  email: string,
  code: string,
  password: string,
): Promise<AuthResponse> {
  const response = await fetch(new URL(`/api/auth/reset-password`, API_URL), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email,
      code,
      password,
      password_confirmation: password,
      source: PersonalAccessTokenSource.Admin,
    }),
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Failed to reset password"),
    );
  }

  return response.json() as Promise<AuthResponse>;
}

export async function getMe(token: string): Promise<MeResponse> {
  const response = await fetch(new URL(`/api/auth/me`, API_URL), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    throw new Error("Session expired");
  }

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, "Failed to fetch user"));
  }

  return response.json() as Promise<MeResponse>;
}

export async function getOrganisations(
  token: string,
): Promise<OrganisationsResponse> {
  const response = await fetch(new URL(`/api/auth/organisations`, API_URL), {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch organisations");
  }

  return response.json() as Promise<OrganisationsResponse>;
}

export async function setPassword(
  token: string,
  password: string,
): Promise<void> {
  const response = await fetch(new URL(`/api/auth/set-password`, API_URL), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      password,
      password_confirmation: password,
    }),
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Failed to set password"),
    );
  }
}

/**
 * @deprecated Reachable only by sessions that signed in before sign up moved to
 * codes - a new account is verified by the code it signs in with.
 */
export async function resendVerification(
  token: string,
  redirect?: string,
): Promise<void> {
  const response = await fetch(
    new URL(`/api/auth/resend-verification`, API_URL),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ redirect }),
    },
  );

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, "Failed to resend verification code"),
    );
  }
}

export async function logout(token: string): Promise<void> {
  await fetch(new URL(`/api/auth/logout`, API_URL), {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).catch((err) => {
    console.error("Logout request failed:", err);
  });
}
